import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Row, Col, Button, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HistoryOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  RiseOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { paymentsAPI, invoicesAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { generationsLabel, generationsWord, generationsWordAccusative } from '../../utils/pluralize';
import {
  findTariffById,
  findTariffByPreselectedType,
} from '../../utils/tariffLandingLink';
import InvoicePaymentModal from './InvoicePaymentModal';
import StackedModal from '../common/StackedModal';
import './Payment.css';

const Modal = StackedModal;

const Payment = ({ user, onBalanceUpdate, preselectedTariffType, preselectedTariffId, onPreselectedTariffConsumed }) => {
  // Состояния для тарифов
  const [tariffs, setTariffs] = useState([]);
  const [loadingTariffs, setLoadingTariffs] = useState(true);
  const [tariffsError, setTariffsError] = useState('');
  
  // Состояния для платежа
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [currentPayment, setCurrentPayment] = useState(null);
  
  // Состояния для модального окна виджета
  const [showWidget, setShowWidget] = useState(false);
  const widgetRef = useRef(null);
  const widgetContainerRef = useRef(null);
  
  // Состояния для результата платежа
  const [paymentResult, setPaymentResult] = useState(null); // 'success' | 'canceled' | 'error' | null
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null); // { oldBalance, newBalance, addedRequests, tariffName, amount }
  
  // Состояния для истории платежей
  const [showHistory, setShowHistory] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showUnavailableTariffModal, setShowUnavailableTariffModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoiceHistory, setLoadingInvoiceHistory] = useState(false);
  const [invoiceDownloadLoading, setInvoiceDownloadLoading] = useState(null);
  
  // Сохраняем текущий баланс перед платежом
  const balanceBeforePaymentRef = useRef(null);
  const preselectionHandledRef = useRef(false);

  // Загрузка тарифов при монтировании
  useEffect(() => {
    loadTariffs();
  }, []);

  // Автооткрытие виджета при переходе с /confirm с предвыбранным тарифом
  useEffect(() => {
    if (loadingTariffs || creatingPayment || showWidget) return;
    if (preselectionHandledRef.current) return;
    if (!preselectedTariffId && !preselectedTariffType) return;

    preselectionHandledRef.current = true;

    let tariff = null;
    if (preselectedTariffId) {
      tariff = findTariffById(tariffs, preselectedTariffId);
    } else if (preselectedTariffType) {
      tariff = findTariffByPreselectedType(tariffs, preselectedTariffType);
    }

    if (tariff) {
      setSelectedTariff(tariff);
      handlePurchase(tariff);
    } else {
      setShowUnavailableTariffModal(true);
    }

    onPreselectedTariffConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffs, loadingTariffs, preselectedTariffType, preselectedTariffId, creatingPayment, showWidget]);

  const loadTariffs = async () => {
    try {
      setLoadingTariffs(true);
      setTariffsError('');
      const response = await paymentsAPI.getTariffs();
      setTariffs(response.tariffs || []);
    } catch (err) {
      setTariffsError('Ошибка загрузки пакетов: ' + getTranslatedError(err));
    } finally {
      setLoadingTariffs(false);
    }
  };

  // Загрузка истории платежей
  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await paymentsAPI.getHistory({ limit: 20 });
      setPayments(response.payments || []);
    } catch (err) {
      console.error('Ошибка загрузки истории платежей:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadInvoiceHistory = async () => {
    try {
      setLoadingInvoiceHistory(true);
      const response = await invoicesAPI.getMyList({ limit: 50 });
      setInvoices(response.invoices || []);
    } catch (err) {
      console.error('Ошибка загрузки счетов:', err);
    } finally {
      setLoadingInvoiceHistory(false);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      setInvoiceDownloadLoading(invoice.id);
      const response = await invoicesAPI.downloadInvoice(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка скачивания счёта:', err);
      alert('Ошибка при скачивании счёта: ' + getTranslatedError(err, 'Неизвестная ошибка'));
    } finally {
      setInvoiceDownloadLoading(null);
    }
  };

  // Создание платежа и открытие виджета
  const handlePurchase = async (tariff) => {
    setSelectedTariff(tariff);
    setCreatingPayment(true);
    setPaymentError('');
    setPaymentResult(null);
    setResultData(null);
    
    // Сохраняем текущий баланс перед платежом
    const currentBalance = user?.requestsBalance || user?.requests_balance || 0;
    balanceBeforePaymentRef.current = currentBalance;
    
    // Сохраняем в localStorage для восстановления после редиректа
    localStorage.setItem('paymentPendingBalance', currentBalance.toString());
    localStorage.setItem('paymentPendingTariff', tariff.name);

    try {
      const response = await paymentsAPI.createPayment(tariff.id);
      
      if (!response.confirmationToken) {
        throw new Error('Не получен токен для оплаты');
      }

      setCurrentPayment({
        id: response.payment.id,
        confirmationToken: response.confirmationToken,
        amount: response.payment.amount,
        requestsCount: response.payment.requests_count || response.payment.requestsCount,
        tariffName: tariff.name,
      });
      
      setShowWidget(true);
    } catch (err) {
      setPaymentError('Ошибка создания платежа: ' + getTranslatedError(err));
      setSelectedTariff(null);
      // Очищаем localStorage при ошибке
      localStorage.removeItem('paymentPendingBalance');
      localStorage.removeItem('paymentPendingTariff');
    } finally {
      setCreatingPayment(false);
    }
  };

  // Инициализация виджета ЮKassa
  const initWidget = useCallback(() => {
    if (!currentPayment?.confirmationToken || !widgetContainerRef.current) {
      return;
    }

    // Очищаем предыдущий виджет если был
    if (widgetRef.current) {
      try {
        widgetRef.current.destroy();
      } catch (e) {
        console.warn('Error destroying widget:', e);
      }
      widgetRef.current = null;
    }

    // Проверяем, что скрипт ЮKassa загружен
    if (!window.YooMoneyCheckoutWidget) {
      setPaymentError('Виджет ЮKassa не загружен. Пожалуйста, обновите страницу.');
      return;
    }

    try {
      const checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: currentPayment.confirmationToken,
        return_url: `${window.location.origin}${window.location.pathname}?paymentId=${currentPayment.id}&paymentComplete=true`,
        
        // Кастомизация цветов под стиль Fixator
        customization: {
          colors: {
            control_primary: '#4F46E5', // Индиго — основной цвет
            background: '#FFFFFF'
          }
        },
        
        error_callback: (error) => {
          console.error('YooKassa widget error:', error);
          setPaymentError('Ошибка виджета оплаты: ' + (error?.message || 'Неизвестная ошибка'));
        }
      });

      // Рендерим виджет в контейнер
      checkout.render('yookassa-widget-container');
      widgetRef.current = checkout;
    } catch (err) {
      console.error('Error initializing widget:', err);
      setPaymentError('Ошибка инициализации виджета: ' + err.message);
    }
  }, [currentPayment]);

  // Инициализация виджета при открытии модалки
  useEffect(() => {
    if (showWidget && currentPayment) {
      // Небольшая задержка для рендеринга DOM
      const timer = setTimeout(() => {
        initWidget();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showWidget, currentPayment, initWidget]);

  // Проверка результата платежа из URL (после редиректа)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('paymentId');
    const paymentComplete = urlParams.get('paymentComplete');

    if (paymentId && paymentComplete === 'true') {
      // Очищаем URL от параметров платежа
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // Восстанавливаем сохранённый баланс и название тарифа из localStorage
      const savedBalance = localStorage.getItem('paymentPendingBalance');
      const savedTariffName = localStorage.getItem('paymentPendingTariff');
      
      if (savedBalance) {
        balanceBeforePaymentRef.current = parseInt(savedBalance, 10);
      }
      
      // Сохраняем название тарифа для отображения в результате
      if (savedTariffName) {
        setSelectedTariff({ name: savedTariffName });
      }
      
      // Проверяем статус платежа
      checkPaymentStatus(paymentId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Проверка статуса платежа (polling)
  const checkPaymentStatus = async (paymentId) => {
    setCheckingStatus(true);
    setShowWidget(false);
    
    const maxAttempts = 90; // Максимум 90 попыток (3 минуты)
    let attempts = 0;
    
    // Получаем данные из localStorage (для случая редиректа)
    const savedTariffName = localStorage.getItem('paymentPendingTariff');
    const savedBalance = localStorage.getItem('paymentPendingBalance');
    const storedOldBalance = savedBalance ? parseInt(savedBalance, 10) : null;

    const poll = async () => {
      try {
        const response = await paymentsAPI.getPaymentStatus(paymentId);
        
        if (response.status === 'succeeded') {
          const addedRequests = response.requestsCount || response.requests_count || 0;
          const oldBalance = balanceBeforePaymentRef.current ?? storedOldBalance ?? (user?.requestsBalance || user?.requests_balance || 0);
          const newBalance = oldBalance + addedRequests;
          const tariffName = currentPayment?.tariffName || savedTariffName || 'Пакет';
          
          // Подготавливаем данные для красивого модального окна
          setResultData({
            oldBalance,
            newBalance,
            addedRequests,
            tariffName,
            amount: response.amount || currentPayment?.amount || 0,
          });
          
          setPaymentResult('success');
          setCheckingStatus(false);
          setShowResultModal(true);
          
          // Обновляем баланс пользователя
          if (onBalanceUpdate) {
            onBalanceUpdate(addedRequests);
          }
          return;
        }
        
        if (response.status === 'canceled') {
          const tariffName = currentPayment?.tariffName || savedTariffName || 'Пакет';
          setResultData({
            oldBalance: balanceBeforePaymentRef.current ?? storedOldBalance ?? (user?.requestsBalance || user?.requests_balance || 0),
            tariffName,
            amount: currentPayment?.amount || 0,
          });
          setPaymentResult('canceled');
          setCheckingStatus(false);
          setShowResultModal(true);
          return;
        }
        
        // Продолжаем polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentResult('error');
          setCheckingStatus(false);
          setShowResultModal(true);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentResult('error');
          setCheckingStatus(false);
          setShowResultModal(true);
        }
      }
    };

    poll();
  };

  // Закрытие модального окна
  const handleCloseWidget = () => {
    if (widgetRef.current) {
      try {
        widgetRef.current.destroy();
      } catch (e) {
        console.warn('Error destroying widget:', e);
      }
      widgetRef.current = null;
    }
    setShowWidget(false);
    
    // Если есть текущий платеж, проверяем его статус
    if (currentPayment?.id) {
      checkPaymentStatus(currentPayment.id);
    }
  };

  // Закрытие результата
  const handleCloseResult = () => {
    setPaymentResult(null);
    setShowResultModal(false);
    setResultData(null);
    setCurrentPayment(null);
    setSelectedTariff(null);
    balanceBeforePaymentRef.current = null;
    
    // Очищаем localStorage от данных платежа
    localStorage.removeItem('paymentPendingBalance');
    localStorage.removeItem('paymentPendingTariff');
    
    loadTariffs(); // Перезагружаем тарифы
  };

  // Форматирование цены
  const formatPrice = (price, { decimals = 0 } = {}) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(price);
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Получение бейджа статуса
  const getStatusBadge = (status) => {
    const badges = {
      succeeded: { bg: 'success', text: 'Оплачен' },
      pending: { bg: 'warning', text: 'Ожидает' },
      waiting_for_capture: { bg: 'info', text: 'Обработка' },
      canceled: { bg: 'danger', text: 'Отменён' },
      refunded: { bg: 'secondary', text: 'Возврат' },
    };
    const badge = badges[status] || { bg: 'secondary', text: status };
    return <Badge bg={badge.bg}>{badge.text}</Badge>;
  };

  const getIneligibilityBadgeLabel = (tariff) => {
    switch (tariff.ineligibilityReason) {
      case 'TARIFF_FIRST_PURCHASE_ONLY':
        return 'Скрыт для тех, кто уже покупал';
      case 'TARIFF_NOT_ELIGIBLE':
        return 'Не назначен этому пользователю';
      case 'TARIFF_ALREADY_USED':
        return 'Акция уже использована';
      case 'TARIFF_INACTIVE':
        return 'Тариф неактивен';
      default:
        if (tariff.isFirstPurchaseOnly) {
          return 'Скрыт для тех, кто уже покупал';
        }
        return 'Недоступен для покупки';
    }
  };

  const getTariffBadges = (tariff) => {
    const badges = [];
    if (tariff.isPromotional) {
      badges.push({ key: 'promo', className: 'tariff-badge tariff-badge--promo', label: 'Акция' });
    }
    if (tariff.isFirstPurchaseOnly) {
      badges.push({ key: 'first', className: 'tariff-badge tariff-badge--first', label: 'Первая покупка' });
    }
    if (tariff.isPersonal) {
      badges.push({ key: 'personal', className: 'tariff-badge tariff-badge--personal', label: 'Для вас' });
    }
    if (user?.role === 'admin' && tariff.eligibleForPurchase === false) {
      badges.push({
        key: 'hidden',
        className: 'tariff-badge tariff-badge--hidden',
        label: getIneligibilityBadgeLabel(tariff),
      });
    }
    return badges;
  };

  const tariffInsights = useMemo(() => {
    if (!tariffs.length) return [];

    const withMetrics = tariffs.map((tariff) => {
      const pricePerRequest = parseFloat(
        tariff.pricePerRequest || (tariff.price / tariff.requestsCount)
      );
      return { tariff, pricePerRequest };
    });

    const baselinePPR = Math.max(...withMetrics.map((item) => item.pricePerRequest));
    const bestPPR = Math.min(...withMetrics.map((item) => item.pricePerRequest));
    const bestTariffId = withMetrics.find((item) => item.pricePerRequest === bestPPR)?.tariff.id;

    return withMetrics.map(({ tariff, pricePerRequest }, index) => {
      const savingsPercent =
        baselinePPR > pricePerRequest
          ? Math.round((1 - pricePerRequest / baselinePPR) * 100)
          : 0;
      const savingsPerRequest = Math.max(0, baselinePPR - pricePerRequest);
      const totalSavings = Math.round(savingsPerRequest * tariff.requestsCount);
      const isBestValue = tariff.id === bestTariffId && tariffs.length > 1;
      const isBaseline = savingsPercent === 0;

      return {
        tariff,
        pricePerRequest,
        savingsPercent,
        savingsPerRequest,
        totalSavings,
        isBestValue,
        isBaseline,
        tierIndex: index,
      };
    });
  }, [tariffs]);

  // Рендер состояния загрузки тарифов
  if (loadingTariffs) {
    return (
      <div className="payment-loading">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Загрузка пакетов...</p>
      </div>
    );
  }

  // Рендер ошибки загрузки тарифов
  if (tariffsError) {
    return (
      <Alert variant="danger" className="mt-3">
        {tariffsError.replace('тарифов', 'пакетов')}
        <Button variant="link" onClick={loadTariffs} className="p-0 ms-2">
          Повторить
        </Button>
      </Alert>
    );
  }

  return (
    <div className="payment-container">
      {/* Заголовок и текущий баланс */}
      <div className="payment-header mb-4">
        <div className="payment-header__row">
          <div className="payment-header__info">
            <h5 className="mb-1">
              <ShoppingCartOutlined className="me-2" />
              Покупка пакетов генераций
            </h5>
            <p className="text-muted mb-0">
              Текущий баланс: <strong>{generationsLabel(user?.requestsBalance ?? user?.requests_balance ?? 0)}</strong>
            </p>
          </div>
          <div className="payment-header__actions">
            <Button
              variant="outline-primary"
              size="sm"
              className="payment-header__invoice-btn me-2"
              onClick={() => setShowInvoiceModal(true)}
            >
              <FileTextOutlined className="me-1" />
              Выставить счёт
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="payment-header__history-btn me-2"
              onClick={() => {
                setShowInvoiceHistory(true);
                loadInvoiceHistory();
              }}
            >
              <FileTextOutlined className="me-1" />
              Мои счета
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="payment-header__history-btn"
              onClick={() => {
                setShowHistory(true);
                loadHistory();
              }}
            >
              <HistoryOutlined className="me-1" />
              История платежей
            </Button>
          </div>
        </div>
      </div>

      {/* Ошибка платежа */}
      {paymentError && (
        <Alert variant="danger" dismissible onClose={() => setPaymentError('')} className="mb-3">
          {paymentError}
        </Alert>
      )}

      {user?.role === 'admin' && tariffs.some((tariff) => tariff.eligibleForPurchase === false) && (
        <Alert variant="secondary" className="mb-3">
          Режим администратора: показаны все тарифы. Красные бейджи поясняют, кому тариф недоступен.
        </Alert>
      )}

      {/* Проверка статуса */}
      {checkingStatus && (
        <Alert variant="info" className="mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Проверяем статус платежа...
        </Alert>
      )}

      {/* Результат платежа теперь показывается в модальном окне */}

      {/* Список тарифов */}
      <Row className="g-3 g-md-4 tariff-grid">
        {tariffInsights.map((insight) => {
          const { tariff, pricePerRequest, savingsPercent, savingsPerRequest, totalSavings, isBestValue, isBaseline } = insight;
          const cardClass = [
            'tariff-card',
            `tariff-card--tier-${insight.tierIndex % 3}`,
            isBestValue ? 'tariff-card--featured' : '',
            selectedTariff?.id === tariff.id ? 'tariff-card--selected' : '',
          ].filter(Boolean).join(' ');

          return (
            <Col key={tariff.id} xs={12} sm={6} lg={4}>
              <article className={cardClass}>
                <div className="tariff-card__top">
                  {isBestValue && (
                    <div className="tariff-card__ribbon">
                      <CrownOutlined />
                      <span>Лучшая цена</span>
                    </div>
                  )}
                  <div className="tariff-card__badges">
                    {getTariffBadges(tariff).map((badge) => (
                      <span key={badge.key} className={badge.className}>
                        {badge.label}
                      </span>
                    ))}
                  </div>

                  <h5 className="tariff-card__name">{tariff.name}</h5>

                  <div className="tariff-card__highlights">
                    <span className="tariff-card__price">{formatPrice(tariff.price)}</span>
                    <span className="tariff-card__requests">
                      <ThunderboltOutlined className="tariff-card__requests-icon" />
                      {tariff.requestsCount} {generationsWord(tariff.requestsCount)}
                    </span>
                  </div>
                </div>

                <div className="tariff-card__body">
                  <div className="tariff-card__unit-price">
                    <span className="tariff-card__unit-label">Цена за {generationsWordAccusative(1)}</span>
                    <span className="tariff-card__unit-value">
                      {formatPrice(pricePerRequest, { decimals: 2 })}
                    </span>
                  </div>

                  {!isBaseline && tariffs.length > 1 && (
                    <div className="tariff-card__savings">
                      <div className="tariff-card__savings-head">
                        <RiseOutlined />
                        <span>На {savingsPercent}% выгоднее</span>
                      </div>
                      <p className="tariff-card__savings-detail">
                        Экономия {formatPrice(savingsPerRequest, { decimals: 2 })} за {generationsWordAccusative(1)}
                        {totalSavings > 0 && (
                          <> · всего ~{formatPrice(totalSavings)}</>
                        )}
                      </p>
                      <div className="tariff-card__savings-bar" aria-hidden="true">
                        <div
                          className="tariff-card__savings-fill"
                          style={{ width: `${Math.min(savingsPercent, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {tariff.description && (
                    <p className="tariff-card__description">{tariff.description}</p>
                  )}
                </div>

                <div className="tariff-card__footer">
                  <Button
                    variant={isBestValue ? 'primary' : 'outline-primary'}
                    className={`tariff-card__btn ${isBestValue ? 'tariff-card__btn--featured' : ''}`}
                    disabled={creatingPayment}
                    onClick={() => handlePurchase(tariff)}
                  >
                    {creatingPayment && selectedTariff?.id === tariff.id ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Создание...
                      </>
                    ) : (
                      <>
                        <ShoppingCartOutlined className="me-2" />
                        Купить пакет
                      </>
                    )}
                  </Button>
                </div>
              </article>
            </Col>
          );
        })}
      </Row>

      {tariffs.length === 0 && (
        <Alert variant="info" className="mt-3">
          Нет доступных пакетов для покупки
        </Alert>
      )}

      <Modal
        show={showUnavailableTariffModal}
        onHide={() => setShowUnavailableTariffModal(false)}
        centered
        className="payment-result-modal"
      >
        <Modal.Body className="text-center py-5">
          <div className="payment-result payment-result--canceled">
            <div className="payment-result__icon payment-result__icon--canceled">
              <CloseCircleOutlined />
            </div>

            <h3 className="payment-result__title">Упс!</h3>

            <p className="payment-result__subtitle text-muted mb-4">
              К сожалению, этот тариф вам недоступен.
              <br />
              Возможно, акция уже использована, предложение было персональным
              или предназначалось только для первой покупки.
            </p>

            <Button
              variant="primary"
              size="lg"
              className="px-5"
              onClick={() => setShowUnavailableTariffModal(false)}
            >
              Понятно
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <InvoicePaymentModal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        tariffs={tariffs}
        user={user}
      />

      {/* Модальное окно с виджетом ЮKassa */}
      <Modal
        show={showWidget}
        onHide={handleCloseWidget}
        centered
        size="lg"
        backdrop="static"
        className="payment-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Оплата пакета «{selectedTariff?.name}»
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="payment-modal-info mb-3">
            <p className="mb-1">
              <strong>Сумма:</strong> {formatPrice(currentPayment?.amount || selectedTariff?.price)}
            </p>
            <p className="mb-0">
              <strong>Будет начислено:</strong> {generationsLabel(currentPayment?.requestsCount ?? selectedTariff?.requestsCount ?? 0)}
            </p>
          </div>
          
          {/* Контейнер для виджета ЮKassa */}
          <div
            id="yookassa-widget-container"
            ref={widgetContainerRef}
            className="yookassa-widget-container"
          />
          
          <p className="text-muted small mt-3 mb-0">
            После оплаты вы будете перенаправлены обратно, и баланс будет начислен автоматически.
          </p>
        </Modal.Body>
      </Modal>

      {/* Модальное окно выставленных счетов */}
      <Modal
        show={showInvoiceHistory}
        onHide={() => setShowInvoiceHistory(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FileTextOutlined className="me-2" />
            Выставленные счета
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingInvoiceHistory ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 mb-0">Загрузка счетов...</p>
            </div>
          ) : invoices.length === 0 ? (
            <Alert variant="info" className="mb-0">
              У вас пока нет выставленных счетов
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Номер</th>
                    <th>Дата</th>
                    <th>Организация</th>
                    <th>Тариф</th>
                    <th>Сумма</th>
                    <th>Статус</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.invoiceNumber}</td>
                      <td>{formatDate(invoice.issuedAt || invoice.createdAt)}</td>
                      <td>{invoice.organizationName}</td>
                      <td>{invoice.tariffName || invoice.tariff?.name || '—'}</td>
                      <td>{formatPrice(invoice.amount)}</td>
                      <td>
                        {invoice.paidAt ? (
                          <Badge bg="success">Оплачен</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Ожидает оплаты</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownloadInvoice(invoice)}
                          disabled={invoiceDownloadLoading === invoice.id}
                        >
                          {invoiceDownloadLoading === invoice.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <DownloadOutlined /> PDF
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowInvoiceHistory(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно истории платежей */}
      <Modal
        show={showHistory}
        onHide={() => setShowHistory(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <HistoryOutlined className="me-2" />
            История платежей
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {user?.email && (
            <p className="payment-history-receipt-note mb-3 small text-muted">
              ЮKassa автоматически отправляет электронные чеки на вашу почту <strong>{user.email}</strong>. Проверьте папку «Спам», если письмо не пришло.
            </p>
          )}
          {loadingHistory ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 mb-0">Загрузка истории...</p>
            </div>
          ) : payments.length === 0 ? (
            <Alert variant="info" className="mb-0">
              История платежей пуста
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Пакет</th>
                    <th>Сумма</th>
                    <th>Генерации</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDate(payment.createdAt || payment.created_at)}</td>
                      <td>{payment.tariff?.name || '—'}</td>
                      <td>{formatPrice(payment.amount)}</td>
                      <td>{payment.requestsCount || payment.requests_count || '—'}</td>
                      <td>{getStatusBadge(payment.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowHistory(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Красивое модальное окно результата платежа */}
      <Modal
        show={showResultModal}
        onHide={handleCloseResult}
        centered
        className="payment-result-modal"
        backdrop="static"
      >
        <Modal.Body className="text-center py-5">
          {paymentResult === 'success' && (
            <div className="payment-result payment-result--success">
              {/* Анимированная иконка успеха */}
              <div className="payment-result__icon payment-result__icon--success">
                <CheckCircleOutlined />
              </div>
              
              <h3 className="payment-result__title">Оплата прошла успешно!</h3>
              
              <p className="payment-result__subtitle text-muted">
                Пакет «{resultData?.tariffName}» активирован
              </p>
              
              {/* Блок с изменением баланса */}
              <div className="balance-change">
                <div className="balance-change__row">
                  <span className="balance-change__label">Было:</span>
                  <span className="balance-change__value balance-change__value--old">
                    {generationsLabel(resultData?.oldBalance ?? 0)}
                  </span>
                </div>
                
                <div className="balance-change__arrow">
                  <GiftOutlined className="balance-change__gift-icon" />
                  <span className="balance-change__added">+{resultData?.addedRequests || 0}</span>
                </div>
                
                <div className="balance-change__row">
                  <span className="balance-change__label">Стало:</span>
                  <span className="balance-change__value balance-change__value--new">
                    {generationsLabel(resultData?.newBalance ?? 0)}
                  </span>
                </div>
              </div>
              
              <div className="payment-result__amount">
                Списано: <strong>{formatPrice(resultData?.amount || 0)}</strong>
              </div>
              
              {user?.email && (
                <div className="payment-result__receipt mt-3">
                  <p className="mb-1 small text-muted">
                    Электронный чек отправлен на вашу почту <strong>{user.email}</strong>
                  </p>
                  <p className="mb-0 small text-muted">
                    Проверьте папку «Спам», если письмо не пришло.
                  </p>
                </div>
              )}
              
              <Button 
                variant="success" 
                size="lg" 
                className="mt-4 px-5"
                onClick={handleCloseResult}
              >
                Отлично!
              </Button>
            </div>
          )}
          
          {paymentResult === 'canceled' && (
            <div className="payment-result payment-result--canceled">
              <div className="payment-result__icon payment-result__icon--canceled">
                <CloseCircleOutlined />
              </div>
              
              <h3 className="payment-result__title">Платёж отменён</h3>
              
              <p className="payment-result__subtitle text-muted">
                Оплата пакета «{resultData?.tariffName}» была отменена
              </p>
              
              <div className="payment-result__info">
                <p className="mb-0">Средства не были списаны с вашей карты.</p>
                <p className="mb-0 text-muted small mt-2">
                  Вы можете попробовать оплатить снова.
                </p>
              </div>
              
              <Button 
                variant="outline-secondary" 
                size="lg" 
                className="mt-4 px-5"
                onClick={handleCloseResult}
              >
                Закрыть
              </Button>
            </div>
          )}
          
          {paymentResult === 'error' && (
            <div className="payment-result payment-result--error">
              <div className="payment-result__icon payment-result__icon--error">
                <CloseCircleOutlined />
              </div>
              
              <h3 className="payment-result__title">Не удалось проверить статус</h3>
              
              <p className="payment-result__subtitle text-muted">
                Произошла ошибка при проверке платежа
              </p>
              
              <div className="payment-result__info">
                <p className="mb-2">
                  Если средства были списаны, баланс будет начислен автоматически в течение нескольких минут.
                </p>
                <p className="mb-0 text-muted small">
                  Если баланс не появился — обратитесь в поддержку.
                </p>
              </div>
              
              <Button 
                variant="outline-secondary" 
                size="lg" 
                className="mt-4 px-5"
                onClick={handleCloseResult}
              >
                Понятно
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Payment;
